import {useEffect, useMemo, useState} from 'react';
import {Cog6ToothIcon} from '@heroicons/react/24/outline';
import {Field, Label} from '@headlessui/react';
import MultiSelectDropdown from '../../../../../components/form/MultiSelectDropdown';
import Popup from '../../../../../components/general/Popup.tsx';
import {SecondaryButton} from '../../../../../components/general/SecondaryButton.tsx';
import {PrimaryButton} from '../../../../../components/general/PrimaryButton.tsx';
import {FilterPendingBadge} from '../../../../../components/general/FilterPendingBadge.tsx';
import Switch from '../../../../../components/general/Switch.tsx';
import {fieldLabelClasses} from '../../../../../components/general/fieldStyles.ts';
import {cn} from '../../../../../lib/utils.ts';
import {DateRangeInput} from '../../../../../components/general/DateRangeInput';
import {
    formatDateForInput,
    parseDate,
} from '../../../../../components/general/DateRangeInput/utils/dateRangeUtils.ts';
import {useFilterStore} from '../../../../../contexts/FilterStore.tsx';
import {
    BILL_TODO_TYPE_LABELS,
    BILL_TODO_TYPE_ORDER,
    TO_CHECK_REASON_LABELS,
    TO_CHECK_REASON_ORDER,
} from '../../../../../types/enums.ts';
import type {DateFilterType} from "../../../../../generated/enums.ts";
import {DocumentsToCheckStatus, splitStatuses} from './documentsToCheck.ts';

const STATUS_LABELS: Record<DocumentsToCheckStatus, string> = {...TO_CHECK_REASON_LABELS, ...BILL_TODO_TYPE_LABELS};

export default function DocumentsToCheckFilterBar({canSeeTodos}: {canSeeTodos: boolean}) {
    const {state, dispatch} = useFilterStore();

    const [dateFrom, setDateFrom] = useState(state.common.dateRange.from);
    const [dateTo, setDateTo] = useState(state.common.dateRange.to);
    const [dateType, setDateType] = useState<DateFilterType>(state.documentsToCheck.dateType);
    const [ignoreDateRange, setIgnoreDateRange] = useState(state.documentsToCheck.ignoreDateRange);
    const [isDateTypePopupOpen, setIsDateTypePopupOpen] = useState(false);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
        [...state.documentsToCheck.reasons, ...state.documentsToCheck.todoTypes],
    );
    const [isDirty, setIsDirty] = useState(false);

    const statusOptions = useMemo(
        () => [...TO_CHECK_REASON_ORDER, ...(canSeeTodos ? BILL_TODO_TYPE_ORDER : [])]
            .map((status) => ({id: status, name: STATUS_LABELS[status]})),
        [canSeeTodos],
    );

    const dateRange = useMemo(() => ({
        from: parseDate(dateFrom),
        to: parseDate(dateTo),
    }), [dateFrom, dateTo]);

    useEffect(() => {
        setDateFrom(state.common.dateRange.from);
        setDateTo(state.common.dateRange.to);
        setDateType(state.documentsToCheck.dateType);
        setIgnoreDateRange(state.documentsToCheck.ignoreDateRange);
        setSelectedStatuses([...state.documentsToCheck.reasons, ...state.documentsToCheck.todoTypes]);
        setIsDirty(false);
    }, [state]);

    const handleDateRangeChange = (range: { from: Date | null, to: Date | null }) => {
        setIsDirty(true);
        setDateFrom(formatDateForInput(range.from));
        setDateTo(formatDateForInput(range.to));
    };

    const handleSubmit = () => {
        setIsDirty(false);
        dispatch({
            type: 'SET_FILTERS',
            payload: {
                common: {
                    dateRange: {from: dateFrom, to: dateTo},
                },
                documentsToCheck: {
                    dateType,
                    ignoreDateRange,
                    ...splitStatuses(selectedStatuses as DocumentsToCheckStatus[]),
                },
            },
        });
    };

    const handleDateTypeChange = (type: DateFilterType) => {
        setIsDirty(true);
        setDateType(type);
        setIsDateTypePopupOpen(false);
    };

    const handleIgnoreDateRangeChange = (checked: boolean) => {
        setIsDirty(true);
        setIgnoreDateRange(checked);
    };

    const handleClear = () => {
        setIsDirty(false);
        dispatch({type: 'RESET_DOCUMENTS_TO_CHECK_FILTERS'});
    };

    return (
        <div className="mb-4 bg-white justify-between w-full flex items-end">
            <div className="flex items-end flex-wrap gap-4">
                <div>
                    <div className="flex items-center mb-2">
                        <button
                            type="button"
                            onClick={() => setIsDateTypePopupOpen(true)}
                            disabled={ignoreDateRange}
                            className="cursor-pointer flex items-center gap-1 text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:text-gray-500"
                        >
                            <span className={fieldLabelClasses}>
                                {dateType === 'invoice'
                                    ? 'Periode - factuurdatum'
                                    : 'Periode - uploaddatum'}
                            </span>
                            <Cog6ToothIcon className="h-4 w-4"/>
                        </button>
                    </div>
                    <div className="w-[234px]">
                        <DateRangeInput value={dateRange} onChange={handleDateRangeChange} disabled={ignoreDateRange}/>
                    </div>
                </div>

                <Popup
                    open={isDateTypePopupOpen}
                    onClose={() => setIsDateTypePopupOpen(false)}
                    onConfirm={() => setIsDateTypePopupOpen(false)}
                    title="Selecteer datum type"
                    Icon={Cog6ToothIcon}
                    confirmButtonDisplayLabel="Sluiten"
                    disableCancelButton={true}
                >
                    <div className="py-2">
                        <div className="flex items-center mb-3">
                            <input
                                id="to-check-invoice-date"
                                type="radio"
                                name="to-check-date-type"
                                value="invoice"
                                checked={dateType === 'invoice'}
                                onChange={() => handleDateTypeChange('invoice')}
                                className="h-4 w-4 text-primary focus:ring-primary"
                            />
                            <label htmlFor="to-check-invoice-date"
                                   className="ml-2 block text-sm font-medium text-gray-700">
                                Periode - factuurdatum
                            </label>
                        </div>
                        <div className="flex items-center">
                            <input
                                id="to-check-upload-date"
                                type="radio"
                                name="to-check-date-type"
                                value="created"
                                checked={dateType === 'created'}
                                onChange={() => handleDateTypeChange('created')}
                                className="h-4 w-4 text-primary focus:ring-primary"
                            />
                            <label htmlFor="to-check-upload-date"
                                   className="ml-2 block text-sm font-medium text-gray-700">
                                Periode - uploaddatum
                            </label>
                        </div>
                    </div>
                </Popup>

                <div className="w-64">
                    <label className={cn(fieldLabelClasses, "mb-2")}>Status</label>
                    <MultiSelectDropdown
                        options={statusOptions}
                        selectedValues={selectedStatuses}
                        onSelectionChange={(values) => {
                            setSelectedStatuses(values);
                            setIsDirty(true);
                        }}
                        placeholder="Alle statussen"
                        displayValue={(value) => STATUS_LABELS[value as DocumentsToCheckStatus] ?? value}
                    />
                </div>

                <Field className="flex h-9 items-center gap-2">
                    <Switch checked={ignoreDateRange} onChange={handleIgnoreDateRangeChange}/>
                    <Label className="cursor-pointer text-sm font-semibold text-gray-700">Negeer periode</Label>
                </Field>
            </div>
            <div className="flex items-center justify-end gap-3">
                <FilterPendingBadge show={isDirty}/>
                <SecondaryButton title={"Wissen"} onClick={handleClear}/>
                <PrimaryButton title={"Toepassen"} onClick={handleSubmit}/>
            </div>
        </div>
    );
}
